import React from 'react';
import { Heart, Leaf, Star, Award, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-wellness-spot/artifacts/y6uqcyxn_1683577696901.png";

const AboutPage = () => {
  const { language } = useLanguage();

  const values = [
    {
      icon: Heart,
      title_pt: 'Bem-Estar Holístico',
      title_en: 'Holistic Wellness',
      description_pt: 'Combinamos técnicas ancestrais africanas com tratamentos modernos para o seu bem-estar completo.',
      description_en: 'We combine ancestral African techniques with modern treatments for your complete well-being.',
    },
    {
      icon: Leaf,
      title_pt: 'Produtos Naturais',
      title_en: 'Natural Products',
      description_pt: 'Utilizamos produtos bio e orgânicos, respeitando a natureza e a sua pele.',
      description_en: 'We use bio and organic products, respecting nature and your skin.',
    },
    {
      icon: Star,
      title_pt: 'Excelência Profissional',
      title_en: 'Professional Excellence',
      description_pt: 'A nossa equipa é formada por profissionais certificados e em constante formação.',
      description_en: 'Our team consists of certified professionals in constant training.',
    },
    {
      icon: Users,
      title_pt: 'Diversidade & Inclusão',
      title_en: 'Diversity & Inclusion',
      description_pt: 'Celebramos a beleza única de cada pessoa, com serviços para todos os tipos de cabelo e pele.',
      description_en: 'We celebrate the unique beauty of each person, with services for all hair and skin types.',
    },
  ];

  return (
    <div className="min-h-screen bg-kamit-beige pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-24 bg-kamit-brown">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-kamit-gold uppercase tracking-widest text-sm mb-4 block">
                {language === 'pt' ? 'A Nossa História' : language === 'fr' ? 'Notre Histoire' : language === 'de' ? 'Unsere Geschichte' : language === 'es' ? 'Nuestra Historia' : 'Our Story'}
              </span>
              <h1 className="font-heading text-5xl md:text-6xl text-kamit-beige mb-4">
                IPRBC KAMIT
              </h1>
              <p 
                className="text-kamit-gold text-2xl md:text-3xl mb-6"
                style={{ fontFamily: "'Tangerine', cursive", fontWeight: 700 }}
              >
                Instituts Professionnels de Relaxations et de Beautés Corporelles
              </p>
              <p 
                className="text-kamit-beige/90 text-xl mb-6"
                style={{ fontFamily: "'Tangerine', cursive", fontWeight: 700 }}
              >
                "Votre fontaine de jouvence"
              </p>
              <p className="text-kamit-beige/70 leading-relaxed mb-6">
                {language === 'pt'
                  ? 'O Instituto Profissional de Relaxações e de Belezas Corporais nasceu da paixão por celebrar a beleza autêntica e o bem-estar de cada pessoa. Localizado na magnífica ilha da Madeira, em Santa Luzia, o IPRBC KAMIT é um refúgio onde tradição e modernidade se encontram.'
                  : language === 'fr'
                  ? 'L\'Institut Professionnel de Relaxations et de Beautés Corporelles est né de la passion de célébrer la beauté authentique et le bien-être de chaque personne. Situé sur la magnifique île de Madère, à Santa Luzia, IPRBC KAMIT est un refuge où tradition et modernité se rencontrent.'
                  : language === 'de'
                  ? 'Das Professionelle Institut für Entspannung und Körperschönheit entstand aus der Leidenschaft, authentische Schönheit und das Wohlbefinden jedes Menschen zu feiern. Auf der wunderschönen Insel Madeira in Santa Luzia gelegen, ist IPRBC KAMIT ein Refugium, wo Tradition und Moderne aufeinandertreffen.'
                  : 'The Professional Institute of Relaxation and Body Beauty was born from a passion for celebrating authentic beauty and the well-being of each person. Located on the magnificent island of Madeira, in Santa Luzia, IPRBC KAMIT is a refuge where tradition and modernity meet.'
                }
              </p>
              <p className="text-kamit-beige/70 leading-relaxed">
                {language === 'pt'
                  ? 'Especializamo-nos em tratamentos de spa, cabelo afro, estética e formações profissionais, trazendo as técnicas ancestrais africanas combinadas com os métodos mais avançados de bem-estar.'
                  : 'We specialize in spa treatments, afro hair, aesthetics and professional training, bringing ancestral African techniques combined with the most advanced wellness methods.'
                }
              </p>
            </div>
            <div className="flex justify-center">
              <img 
                src={LOGO_URL}
                alt="IPRBC KAMIT Logo"
                className="w-96 h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Full Name */}
      <section className="py-12 bg-kamit-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p 
            className="text-kamit-brown text-3xl md:text-4xl"
            style={{ fontFamily: "'Tangerine', cursive", fontWeight: 700 }}
          >
            Instituts Professionnels de Relaxations et de Beautés Corporelles
          </p>
        </div>
      </section>

      {/* Our History / Notre Histoire */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-kamit-gold uppercase tracking-widest text-sm mb-4 block">
              {language === 'fr' ? 'Notre Histoire' : language === 'de' ? 'Unsere Geschichte' : language === 'pt' ? 'A Nossa História' : language === 'es' ? 'Nuestra Historia' : 'Our Story'}
            </span>
            <h2 className="font-heading text-4xl text-kamit-brown mb-4">
              {language === 'fr' ? 'Depuis 2016' : language === 'de' ? 'Seit 2016' : language === 'pt' ? 'Desde 2016' : language === 'es' ? 'Desde 2016' : 'Since 2016'}
            </h2>
            <div className="w-24 h-1 bg-kamit-gold mx-auto" />
          </div>
          
          <div className="bg-kamit-beige/30 rounded-2xl p-8 md:p-12 border border-kamit-gold/20">
            <p className="text-kamit-brown/80 leading-relaxed text-lg mb-6">
              {language === 'fr'
                ? "Née au Cameroun en 2016 dans la charmante ville d'Edéa, IPRBC KAMIT incarne depuis ses origines la fusion parfaite entre héritage africain et excellence contemporaine."
                : language === 'de'
                ? "IPRBC KAMIT wurde 2016 in der charmanten Stadt Edéa in Kamerun gegründet und verkörpert seit seinen Anfängen die perfekte Verschmelzung von afrikanischem Erbe und zeitgenössischer Exzellenz."
                : language === 'pt'
                ? "Nascida nos Camarões em 2016 na encantadora cidade de Edéa, a IPRBC KAMIT encarna desde as suas origens a fusão perfeita entre herança africana e excelência contemporânea."
                : language === 'es'
                ? "Nacida en Camerún en 2016 en la encantadora ciudad de Edéa, IPRBC KAMIT encarna desde sus orígenes la fusión perfecta entre herencia africana y excelencia contemporánea."
                : "Born in Cameroon in 2016 in the charming city of Edéa, IPRBC KAMIT has embodied since its origins the perfect fusion between African heritage and contemporary excellence."
              }
            </p>
            <p className="text-kamit-brown/80 leading-relaxed text-lg mb-6">
              {language === 'fr'
                ? "Notre expertise unique réunit trois piliers essentiels : la coiffure afro authentique, l'esthétique bio-organique respectueuse de votre peau, et l'art ancestral de la massothérapie."
                : language === 'de'
                ? "Unsere einzigartige Expertise vereint drei wesentliche Säulen: authentische Afro-Frisuren, hautfreundliche bio-organische Ästhetik und die uralte Kunst der Massagetherapie."
                : language === 'pt'
                ? "A nossa expertise única reúne três pilares essenciais: o penteado afro autêntico, a estética bio-orgânica respeitosa da sua pele, e a arte ancestral da massoterapia."
                : language === 'es'
                ? "Nuestra experiencia única reúne tres pilares esenciales: el peinado afro auténtico, la estética bio-orgánica respetuosa con tu piel, y el arte ancestral de la masoterapia."
                : "Our unique expertise brings together three essential pillars: authentic Afro hairstyling, skin-friendly bio-organic aesthetics, and the ancestral art of massage therapy."
              }
            </p>
            <p className="text-kamit-brown/80 leading-relaxed text-lg">
              {language === 'fr'
                ? "Notre philosophie ? Révéler votre beauté naturelle en mariant harmonieusement les rituels de beauté traditionnels aux innovations modernes. Aujourd'hui installés à Madère, nous perpétuons cette vision : vous offrir une expérience de bien-être holistique, personnalisée et profondément enracinée dans nos traditions."
                : language === 'de'
                ? "Unsere Philosophie? Ihre natürliche Schönheit zu enthüllen, indem wir traditionelle Schönheitsrituale harmonisch mit modernen Innovationen verbinden. Heute auf Madeira ansässig, setzen wir diese Vision fort: Ihnen ein ganzheitliches, personalisiertes und tief in unseren Traditionen verwurzeltes Wellness-Erlebnis zu bieten."
                : language === 'pt'
                ? "A nossa filosofia? Revelar a sua beleza natural casando harmoniosamente os rituais de beleza tradicionais com as inovações modernas. Hoje instalados na Madeira, perpetuamos esta visão: oferecer-lhe uma experiência de bem-estar holística, personalizada e profundamente enraizada nas nossas tradições."
                : language === 'es'
                ? "¿Nuestra filosofía? Revelar tu belleza natural combinando armoniosamente los rituales de belleza tradicionales con las innovaciones modernas. Hoy instalados en Madeira, perpetuamos esta visión: ofrecerte una experiencia de bienestar holística, personalizada y profundamente arraigada en nuestras tradiciones."
                : "Our philosophy? To reveal your natural beauty by harmoniously combining traditional beauty rituals with modern innovations. Now established in Madeira, we continue this vision: to offer you a holistic, personalized wellness experience deeply rooted in our traditions."
              }
            </p>
          </div>
          
          <div className="flex justify-center mt-8">
            <div className="flex items-center gap-8 text-kamit-brown/60">
              <div className="text-center">
                <p className="font-heading text-3xl text-kamit-gold">2016</p>
                <p className="text-sm">Cameroun</p>
              </div>
              <div className="w-16 h-px bg-kamit-gold" />
              <div className="text-center">
                <p className="font-heading text-3xl text-kamit-gold">{language === 'fr' ? 'Aujourd\'hui' : 'Today'}</p>
                <p className="text-sm">Madeira, Portugal</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl text-kamit-brown mb-4">
              {language === 'pt' ? 'Os Nossos Valores' : language === 'fr' ? 'Nos Valeurs' : language === 'de' ? 'Unsere Werte' : language === 'es' ? 'Nuestros Valores' : 'Our Values'}
            </h2>
            <div className="w-24 h-1 bg-kamit-gold mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div 
                  key={index}
                  className="text-center p-6 rounded-xl bg-white shadow-sm hover:shadow-lg transition-shadow"
                >
                  <div className="w-16 h-16 bg-kamit-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="h-8 w-8 text-kamit-gold" />
                  </div>
                  <h3 className="font-heading text-xl text-kamit-brown mb-3">
                    {language === 'pt' ? value.title_pt : value.title_en}
                  </h3>
                  <p className="text-kamit-brown/70 text-sm leading-relaxed">
                    {language === 'pt' ? value.description_pt : value.description_en}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-20 bg-kamit-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl text-kamit-brown mb-4">
              {language === 'fr' ? 'La Fondatrice' : language === 'pt' ? 'A Fundadora' : language === 'de' ? 'Die Gründerin' : language === 'es' ? 'La Fundadora' : 'The Founder'}
            </h2>
            <div className="w-24 h-1 bg-kamit-gold mx-auto" />
          </div>
          
          <div className="bg-white rounded-2xl overflow-hidden shadow-xl">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Photo */}
              <div className="relative h-80 md:h-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_7f45736e-df57-4742-9820-4e4efb4e31c0/artifacts/w2w15me5_ChatGPT%20Image%2025%20janv.%202026%2C%2023_51_32.png"
                  alt="Esther Celine Gwack - Founder"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-kamit-dark/30 to-transparent md:bg-gradient-to-r" />
              </div>
              
              {/* Content */}
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <div className="mb-6">
                  <p className="text-kamit-gold uppercase tracking-widest text-sm mb-2">
                    {language === 'fr' ? 'Fondatrice & Formatrice' : language === 'pt' ? 'Fundadora & Formadora' : 'Founder & Trainer'}
                  </p>
                  <h3 className="font-heading text-3xl text-kamit-brown mb-1">Esther Celine Gwack</h3>
                  <p 
                    className="text-kamit-gold text-lg"
                    style={{ fontFamily: "'Tangerine', cursive", fontWeight: 700 }}
                  >
                    E.S.T.H.E.R.C. Director
                  </p>
                </div>
                
                <p className="text-kamit-brown/70 leading-relaxed mb-4">
                  {language === 'fr'
                    ? "Experte en techniques holistiques d'esthétique et de relaxation corporelle, Esther Celine Gwack a fondé IPRBC KAMIT avec la mission d'apporter les meilleurs soins de bien-être à Madère, en alliant traditions africaines ancestrales et innovations modernes."
                    : language === 'pt'
                    ? 'Especialista em técnicas holísticas de estética e relaxamento corporal, Esther Celine Gwack fundou o IPRBC KAMIT com a missão de trazer os melhores tratamentos de bem-estar para a Madeira, aliando tradições africanas ancestrais e inovações modernas.'
                    : 'Expert in holistic aesthetic and body relaxation techniques, Esther Celine Gwack founded IPRBC KAMIT with the mission of bringing the best wellness treatments to Madeira, combining ancestral African traditions with modern innovations.'
                  }
                </p>
                <p className="text-kamit-brown/70 leading-relaxed mb-6">
                  {language === 'fr'
                    ? "À travers l'E.S.T.H.E.R.C., elle offre des formations professionnelles certifiées dans divers domaines de la beauté et du bien-être."
                    : language === 'pt'
                    ? 'Através da E.S.T.H.E.R.C., oferece formações profissionais certificadas em diversas áreas da beleza e bem-estar.'
                    : 'Through E.S.T.H.E.R.C., she offers certified professional training in various areas of beauty and wellness.'
                  }
                </p>
                
                <div className="flex flex-wrap gap-3">
                  <span className="px-4 py-2 bg-kamit-gold/10 text-kamit-brown rounded-full text-sm">
                    {language === 'fr' ? 'Massothérapie' : 'Massage Therapy'}
                  </span>
                  <span className="px-4 py-2 bg-kamit-gold/10 text-kamit-brown rounded-full text-sm">
                    {language === 'fr' ? 'Esthétique Bio' : 'Bio Aesthetics'}
                  </span>
                  <span className="px-4 py-2 bg-kamit-gold/10 text-kamit-brown rounded-full text-sm">
                    Afro Hair
                  </span>
                  <span className="px-4 py-2 bg-kamit-gold/10 text-kamit-brown rounded-full text-sm">
                    {language === 'fr' ? 'Formatrice Certifiée' : 'Certified Trainer'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-20 bg-kamit-brown">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl text-kamit-beige mb-6">
            {language === 'pt' ? 'Visite-nos na Madeira' : 'Visit Us in Madeira'}
          </h2>
          <p className="text-kamit-beige/70 text-lg mb-8">
            {language === 'pt'
              ? 'Estamos localizados em Santa Luzia, num espaço acolhedor e tranquilo, perfeito para o seu momento de relaxamento e beleza.'
              : 'We are located in Santa Luzia, in a cozy and peaceful space, perfect for your moment of relaxation and beauty.'
            }
          </p>
          <div className="bg-kamit-dark/30 backdrop-blur-sm rounded-2xl p-8 inline-block">
            <p className="text-kamit-beige font-medium text-lg">
              Rua da Ponte Nova N° 27<br />
              Loja A, Rés de Chão<br />
              9050-440 Santa Luzia<br />
              Madeira, Portugal
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
